import { DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReplaySubject } from 'rxjs';

import { IndividualService } from './individual.service';
import { ExecutionStatus, Individual } from '../../proxy/models/common';
import { UserService } from './user.service';
import { SessionService } from './session.service';
import { AllLookupsService } from './all-lookups.service';
import { LogoutService } from '../../proxy/services/logout.service';
import { GlobalConfigsService } from './global-configs.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    public destroyRef = inject(DestroyRef);
    private isAuthenticatedSubject$ = new ReplaySubject<boolean>(1);
    public isAuthenticated = this.isAuthenticatedSubject$.asObservable();

    constructor(
        private individualService: IndividualService,
        private userService: UserService,
        private lookupsService: AllLookupsService,
        private sessionService: SessionService,
        private configsService: GlobalConfigsService,
        private router: Router,
        private logoutService: LogoutService
    ) {
    }

    public async checkAuth(): Promise<void> {
        const individual = this.individualService.getIndividual();
        if (individual) {
            this.setIndividual(individual);
            this.isAuthenticatedSubject$.next(true);
        } else {
            this.isAuthenticatedSubject$.next(false);
        }
    }

    public setIndividual(individual: Individual): void {
        this.individualService.saveIndividual(individual);
    }

    public async login(returnUrl?: string): Promise<void> {
        this.isAuthenticatedSubject$.next(true);
        await this.router.navigate([returnUrl || '/dashboard']);
    }

    public logout(cb?: Function): void {
        this.logoutService.logout().pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe((res) => {
            if (res.result.status === ExecutionStatus.success) {
                this.removeStorage();
                cb && cb();
            }
        });
    }

    public removeStorage(): void {
        this.individualService.removeIndividual();
        this.userService.removeUser();
        this.sessionService.removeSessionId();
        this.isAuthenticatedSubject$.next(false);
        this.configsService.removeConfigs();
        this.lookupsService.removeLookups();
    }
}
